import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable, NgZone } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { catchError, Observable, retry, take, throwError } from 'rxjs';

import { CCAuthService } from '@heidelberg/control-center-frontend-integration/auth';

import { ROUTE_PATH } from '@data-terminal/shared-models/navigation';
import {
    InfoDialogComponent,
    InfoDialogData,
    RefreshPageDialogComponent,
} from '@data-terminal/ui-presentational/dialogs';
import { ErrorDisplayService } from '@data-terminal/data-access/error/error-display.service';

/**
 * Global error handler, if an uncaught error occurs.
 * Shows the error in the Snackbar
 */
@Injectable()
export class GlobalErrorHandler implements HttpInterceptor {
    private readonly ngZone = inject(NgZone);
    private readonly dialog = inject(MatDialog);
    private readonly ccAuthService = inject(CCAuthService);
    private readonly router = inject(Router);
    private readonly errorDisplayService = inject(ErrorDisplayService);

    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(request).pipe(
            retry(1),
            catchError((e: HttpErrorResponse) => {
                try {
                    e = JSON.parse(e.error);
                } catch (_e) {
                    // Don't convert to json. Keep as string.
                }
                this.ngZone.run(() => {
                    if (e.message.indexOf('ChunkLoadError') >= 0 && this.ccAuthService.isAuthenticated()) {
                        this.showReloadDialog(this.dialog);
                        return;
                    }

                    const NOT_FOUND_STATUS = 404;

                    if (e.status === NOT_FOUND_STATUS && e.error?.message === 'Person not found') {
                        const baseDialogRef = this.dialog.open<InfoDialogComponent, InfoDialogData>(
                            InfoDialogComponent,
                            {
                                data: {
                                    text: 'DC.USER_NOT_FOUND.HEADER',
                                    type: 'error',
                                },
                            }
                        );

                        baseDialogRef
                            .afterClosed()
                            .pipe(take(1))
                            .subscribe((_) => {
                                this.ccAuthService.localSignOut();
                            });
                    } else if (this.ccAuthService.isAuthenticated()) {
                        this.errorDisplayService.displayErrorMessage(e);
                        this.router.navigate([ROUTE_PATH.error], { state: { error: e } });
                    }
                });

                return throwError(() => e);
            })
        );
    }

    private showReloadDialog(dialog: MatDialog): void {
        dialog.open(RefreshPageDialogComponent);
    }
}
